// src/components/GenerateProductAvatarVideoModal.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import { X, RefreshCcw, ImagePlus } from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import Loader from "./Loader"; // ✅ use the same loader

const PAGE_SIZE = 32;
const SCRIPT_CHAR_LIMIT = 400;

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://ecom-ads-ai-backend-production.up.railway.app";

const LANGUAGE_FLAG_MAP = {
  en: "🇺🇸",
  ar: "🇸🇦",
  bg: "🇧🇬",
  hr: "🇭🇷",
  cs: "🇨🇿",
  da: "🇩🇰",
  nl: "🇳🇱",
  fil: "🇵🇭",
  fi: "🇫🇮",
  fr: "🇫🇷",
  de: "🇩🇪",
  el: "🇬🇷",
  hi: "🇮🇳",
  hu: "🇭🇺",
  id: "🇮🇩",
  it: "🇮🇹",
  ja: "🇯🇵",
  ko: "🇰🇷",
  ms: "🇲🇾",
  nb: "🇳🇴",
  pl: "🇵🇱",
  pt: "🇵🇹",
  ro: "🇷🇴",
  ru: "🇷🇺",
  "zh-CN": "🇨🇳",
  sk: "🇸🇰",
  es: "🇪🇸",
  sv: "🇸🇪",
  "zh-Hant": "🇹🇼",
  tr: "🇹🇷",
  uk: "🇺🇦",
  vi: "🇻🇳",
  th: "🇹🇭",
};

/* ---------------- Module-level caches ---------------- */
// cache voices per filter combination: `${lang||all}_${gender||all}_${age||all}`
const voicesCache = {}; // { [key]: { voices, hasMore, page } }

// captions are basically static; cache once per session
const captionsCache = {
  list: null,
};

// voice filters cache (same as AdMarketing)
const cache = {
  voiceFilters: null,
};

// --- Sticky Footer ---
function StepFooter({ left, right }) {
  return (
    <div className="absolute bottom-0 left-0 w-full bg-[#191930] border-t border-[#25254A] flex justify-between items-center px-8 py-5 z-20">
      <div>{left}</div>
      <div>{right}</div>
    </div>
  );
}

function clampScript(text, limit = SCRIPT_CHAR_LIMIT) {
  return String(text || "").replace(/\s+/g, " ").trim().slice(0, limit);
}

export default function GenerateProductAvatarVideoModal({
  projectId,
  onClose,
  refreshVideos,
}) {
  const { getToken } = useAuth();

  // Stepper
  const [step, setStep] = useState(1);

  // Step 1: Avatar image
  const [avatarImages, setAvatarImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(true);
  const [imagesError, setImagesError] = useState("");
  const [selectedAvatarImage, setSelectedAvatarImage] = useState(null);

  // Step 2: Script
  const [script, setScript] = useState("");
  const [generatingScript, setGeneratingScript] = useState(false);
  const [scriptError, setScriptError] = useState("");
  const scriptLength = script.length;
  const scriptNearLimit = scriptLength >= 320;

  // Step 3: Voices
  const [voices, setVoices] = useState([]);
  const [voicePage, setVoicePage] = useState(1);
  const [hasMoreVoices, setHasMoreVoices] = useState(true);
  const [selectedVoiceId, setSelectedVoiceId] = useState(null);

  const [allLanguages, setAllLanguages] = useState([]);
  const [allVoiceGenders, setAllVoiceGenders] = useState([]);
  const [allAges, setAllAges] = useState([]);

  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedVoiceGender, setSelectedVoiceGender] = useState("");
  const [selectedAge, setSelectedAge] = useState("");

  const [voiceError, setVoiceError] = useState("");
  const [voicesLoading, setVoicesLoading] = useState(false);

  // Step 3: Captions
  const [captions, setCaptions] = useState([]);
  const [selectedCaptionId, setSelectedCaptionId] = useState("");
  const [captionsLoading, setCaptionsLoading] = useState(false);
  const [captionError, setCaptionError] = useState("");

  // Global audio control (same pattern as AdMarketing)
  const currentlyPlayingAudioRef = useRef(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);

  const onVoicePlay = useCallback((audioRef, voiceId) => {
    if (
      currentlyPlayingAudioRef.current &&
      currentlyPlayingAudioRef.current !== audioRef.current
    ) {
      try {
        currentlyPlayingAudioRef.current.pause();
      } catch {
        // ignore
      }
    }
    currentlyPlayingAudioRef.current = audioRef.current;
    setPlayingAudioId(voiceId);
  }, []);

  const onVoicePause = useCallback(() => {
    if (currentlyPlayingAudioRef.current) {
      try {
        currentlyPlayingAudioRef.current.pause();
      } catch {
        // ignore
      }
    }
    currentlyPlayingAudioRef.current = null;
    setPlayingAudioId(null);
  }, []);

  // General
  const [generating, setGenerating] = useState(false);

  // Helpful flags (same style as image modal)
  const isImagesInitialLoading =
    imagesLoading && !imagesError && avatarImages.length === 0;

  const isVoicesInitialLoading =
    step === 3 && voicesLoading && !voiceError && voices.length === 0;

  // voice cache key (per filter combo)
  const voiceCacheKey = `${selectedLanguage || "all"}_${
    selectedVoiceGender || "all"
  }_${selectedAge || "all"}`;

  // --- Step 1: Load avatar images ---
  useEffect(() => {
    let mounted = true;
    async function fetchImages() {
      setImagesLoading(true);
      setImagesError("");
      try {
        const token = await getToken();
        const res = await axios.get(
          `${BACKEND_URL}/api/product-avatar-video/images`,
          {
            params: { projectId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!mounted) return;

        const raw = Array.isArray(res.data?.images)
          ? res.data.images
          : Array.isArray(res.data)
          ? res.data
          : [];

        // Keep only real product-avatar-image assets / Cloudinary URLs
        const filtered = raw.filter((img) => {
          if (!img || !img.url) return false;
          const url = String(img.url);

          // Drop any analyzed_video CloudFront links
          if (
            url.includes("analyzed_video%2Fvideo") ||
            url.includes("/analyzed_video/video")
          ) {
            return false;
          }

          // If feature is present, require product-avatar-image
          const feature = img.metadata?.feature || img.feature;
          if (feature && feature !== "product-avatar-image") {
            return false;
          }

          // Prefer Cloudinary avatar images
          if (!url.includes("res.cloudinary.com")) return false;

          return true;
        });

        setAvatarImages(filtered);
        if (filtered.length > 0) {
          setSelectedAvatarImage(filtered[0]);
        }
      } catch (e) {
        setImagesError("Failed to load avatar images.");
        setAvatarImages([]);
      } finally {
        setImagesLoading(false);
      }
    }
    if (projectId) fetchImages();
    return () => {
      mounted = false;
    };
  }, [getToken, projectId]);

  // --- Step 2: Generate script ---
  const handleGenerateScript = useCallback(async () => {
    if (!selectedAvatarImage) return;
    setGeneratingScript(true);
    setScriptError("");
    try {
      const token = await getToken();
      const res = await axios.post(
        `${BACKEND_URL}/api/product-avatar-video/generate-script`,
        {
          projectId,
          productReplaceResultKey: selectedAvatarImage.productReplaceResultKey,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setScript(clampScript(res.data.script || "Presenting our latest product!"));
    } catch {
      setScript("Presenting our latest product!");
      setScriptError("Failed to generate script.");
    } finally {
      setGeneratingScript(false);
    }
  }, [getToken, projectId, selectedAvatarImage]);

  useEffect(() => {
    if (step === 2 && selectedAvatarImage && !script && !generatingScript) {
      handleGenerateScript();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedAvatarImage]);

  // --- Step 3: Voice filters & fetch (same pattern as AdMarketing) ---
  useEffect(() => {
    async function fetchVoiceFilters() {
      // ✅ use cached filters if available
      if (cache.voiceFilters) {
        setAllLanguages(cache.voiceFilters.languages);
        setAllVoiceGenders(cache.voiceFilters.genders);
        setAllAges(cache.voiceFilters.ages);
        return;
      }

      try {
        const token = await getToken();
        const res = await axios.get(
          `${BACKEND_URL}/api/topview/voices/filters`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const languages = res.data.languages || [];
        const genders = res.data.genders || [];
        const ages = res.data.ages || [];
        setAllLanguages(languages);
        setAllVoiceGenders(genders);
        setAllAges(ages);
        cache.voiceFilters = {
          languages,
          genders,
          ages,
        };
      } catch {
        setVoiceError("Failed to load voice filters.");
      }
    }
    fetchVoiceFilters();
  }, [getToken]);

  const fetchVoices = useCallback(
    async (page = 1, append = false) => {
      setVoicesLoading(true);
      setVoiceError("");

      // ✅ Serve from cache for page 1 when available (per filter combo)
      if (page === 1 && !append && voicesCache[voiceCacheKey]) {
        const cached = voicesCache[voiceCacheKey];
        const cachedVoices = cached.voices || [];
        setVoices(cachedVoices);
        setHasMoreVoices(!!cached.hasMore);
        setVoicePage(cached.page || 1);

        if (cachedVoices.length > 0 && !selectedVoiceId) {
          setSelectedVoiceId(String(cachedVoices[0].id));
        }

        setVoicesLoading(false);
        return;
      }

      try {
        const token = await getToken();
        const res = await axios.get(`${BACKEND_URL}/api/topview/voices`, {
          params: {
            pageNo: page,
            pageSize: PAGE_SIZE,
            language: selectedLanguage || undefined,
            gender: selectedVoiceGender || undefined,
            age: selectedAge || undefined,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const newVoices = Array.isArray(res.data)
          ? res.data.map((v) => ({
              id: String(v.voiceId || v.id),
              name: v.voiceName || v.name,
              gender: v.gender,
              age: v.age,
              accent: v.accent,
              preview_url: v.demoAudioUrl || v.preview_url,
              country: v.country,
              language: v.language,
            }))
          : [];

        const existing = append
          ? voicesCache[voiceCacheKey]?.voices || []
          : [];
        const updatedVoices = append
          ? [...existing, ...newVoices]
          : newVoices;

        const hasMore = newVoices.length === PAGE_SIZE;

        setVoices(updatedVoices);
        setHasMoreVoices(hasMore);
        setVoicePage(page);

        // ✅ Save per-filter in cache
        voicesCache[voiceCacheKey] = {
          voices: updatedVoices,
          hasMore,
          page,
        };

        if (updatedVoices.length > 0 && !selectedVoiceId) {
          setSelectedVoiceId(String(updatedVoices[0].id));
        }
      } catch {
        setVoiceError("Failed to fetch voices.");
      } finally {
        setVoicesLoading(false);
      }
    },
    [
      getToken,
      selectedLanguage,
      selectedVoiceGender,
      selectedAge,
      selectedVoiceId,
      voiceCacheKey,
    ]
  );

  useEffect(() => {
    if (step === 3) {
      fetchVoices(1, false);
    }
  }, [step, selectedLanguage, selectedVoiceGender, selectedAge, fetchVoices]);

  const loadMoreVoices = () => {
    if (!voicesLoading && hasMoreVoices) fetchVoices(voicePage + 1, true);
  };

  const handleVoiceSelect = (voiceId) => {
    setSelectedVoiceId(voiceId);
    // we don't auto-stop preview here; VoicePreviewButton handles that via callbacks
  };

  // --- Step 3: Captions list ---
  useEffect(() => {
    if (step !== 3) return;
    let mounted = true;

    async function fetchCaptions() {
      setCaptionsLoading(true);
      setCaptionError("");

      // ✅ Serve from cache if available
      if (captionsCache.list && captionsCache.list.length > 0) {
        const cached = captionsCache.list;
        setCaptions(cached);
        setSelectedCaptionId((prev) => prev || cached[0]?.id || "");
        setCaptionsLoading(false);
        return;
      }

      try {
        const token = await getToken();
        const res = await axios.get(
          `${BACKEND_URL}/api/product-avatar-video/captions`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!mounted) return;

        const list = Array.isArray(res.data?.result)
          ? res.data.result
          : Array.isArray(res.data)
          ? res.data
          : [];

        const mapped = list.map((c, idx) => {
          const rawId = String(c.captionId || c.id || idx);
          const labelIndex = String(idx + 1).padStart(2, "0");
          return {
            id: rawId,
            name: `Style ${labelIndex}`,
            thumbnail:
              c.thumbnail ||
              c.thumbnailUrl ||
              c.captionImageUrl ||
              c.imageUrl ||
              c.previewImageUrl ||
              c.iconUrl ||
              "",
          };
        });

        // ✅ store in cache
        captionsCache.list = mapped;

        setCaptions(mapped);
        if (mapped.length > 0) {
          setSelectedCaptionId((prev) => prev || mapped[0].id);
        }
      } catch {
        setCaptionError("Failed to load captions (optional).");
        setCaptions([]);
        setSelectedCaptionId("");
      } finally {
        setCaptionsLoading(false);
      }
    }

    fetchCaptions();

    return () => {
      mounted = false;
    };
  }, [getToken, step]);

  // --- Final submit ---
  const handleGenerate = async () => {
    const normalizedScript = clampScript(script);

    if (!selectedAvatarImage || !normalizedScript || !selectedVoiceId) {
      setScriptError(
        "Please make sure you selected an avatar image, have a script and a voice."
      );
      return;
    }

    setGenerating(true);
    setScriptError("");

    const meta = selectedAvatarImage.metadata || {};

    const replaceProductTaskId =
      selectedAvatarImage.replaceProductTaskId ||
      meta.replaceProductTaskId ||
      meta.replaceTaskId ||
      selectedAvatarImage.replaceTaskId ||
      selectedAvatarImage.taskId ||
      meta.taskId ||
      null;

    const replaceProductTaskCardId =
      selectedAvatarImage.replaceProductTaskCardId ||
      selectedAvatarImage.productReplaceResultKey ||
      selectedAvatarImage.replaceProductTaskImageId ||
      meta.replaceProductTaskCardId ||
      meta.productReplaceResultKey ||
      meta.replaceProductTaskImageId ||
      meta.cardId ||
      meta.imageId ||
      null;

    try {
      const token = await getToken();
      await axios.post(
        `${BACKEND_URL}/api/product-avatar-video/generate`,
        {
          projectId,
          productReplaceResultKey: selectedAvatarImage.productReplaceResultKey,
          replaceProductTaskImageId:
            selectedAvatarImage.replaceProductTaskImageId,
          replaceProductTaskId,
          replaceProductTaskCardId,
          script: normalizedScript,
          voiceId: selectedVoiceId,
          captionId: selectedCaptionId || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onClose();
      refreshVideos?.();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to generate video.";
      setScriptError(msg);
    } finally {
      setGenerating(false);
    }
  };

  // --- RENDER ---
  return (
    <div className="fixed z-50 inset-0 flex items-center justify-center bg-black/60 px-2 py-4">
      <div className="relative bg-[#191930] w-[880px] max-w-[98vw] h-[82vh] rounded-2xl shadow-2xl flex flex-col min-h-[520px] overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-white text-black p-2 rounded-full hover:bg-gray-100 z-10"
        >
          <X size={22} />
        </button>

        {/* Stepper */}
        <div className="flex items-center gap-2 px-8 pt-7 pb-2">
          <span
            className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold
            ${
              step === 1
                ? "bg-pink-600 text-white"
                : "bg-[#29294a] text-gray-400"
            }`}
          >
            1
          </span>
          <span className="text-base sm:text-lg text-white font-semibold mr-8">
            Avatar Image
          </span>
          <span
            className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold
            ${
              step === 2
                ? "bg-pink-600 text-white"
                : "bg-[#29294a] text-gray-400"
            }`}
          >
            2
          </span>
          <span className="text-base sm:text-lg text-white font-semibold mr-8">
            Script
          </span>
          <span
            className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold
            ${
              step === 3
                ? "bg-pink-600 text-white"
                : "bg-[#29294a] text-gray-400"
            }`}
          >
            3
          </span>
          <span className="text-base sm:text-lg text-white font-semibold">
            Voice & Captions
          </span>
        </div>

        {/* Main content */}
        <div
          id="product-avatar-video-modal-scroll"
          className="flex-1 relative overflow-y-auto px-8 pb-44 pt-2"
        >
          {/* Step 1: Avatar Image */}
          {step === 1 && (
            <>
              <h2 className="text-xl font-bold text-white mb-3">
                Choose a generated avatar image
              </h2>

              {isImagesInitialLoading ? (
                <div className="flex items-center justify-center py-10 sm:py-16">
                  <Loader />
                </div>
              ) : imagesLoading ? (
                <div className="text-gray-400 text-center py-8">
                  <img
                    src="/spinner.gif"
                    alt="Loading..."
                    className="w-10 h-10 mx-auto mb-4"
                  />
                  Loading avatar images...
                </div>
              ) : avatarImages.length === 0 ? (
                <div className="flex flex-col items-center w-full mt-10">
                  <div className="w-full flex flex-col items-center justify-center border-2 border-dashed border-[#3A3A50] rounded-xl min-h-[140px] py-8 bg-[#191930]/60 max-w-[380px] mx-auto">
                    <span className="text-white font-semibold text-base mb-2">
                      No avatar images found
                    </span>
                    <span className="text-gray-400 text-sm mb-5 text-center max-w-xs">
                      You need to generate a product avatar image before you can
                      create a video.
                    </span>
                    <button
                      onClick={() => {
                        onClose?.();
                        setTimeout(() => {
                          window.location.href = `/generate/product-avatar-image/${projectId}`;
                        }, 100);
                      }}
                      className="mt-2 bg-[#23233a] text-white font-bold px-6 py-2 rounded-xl border border-[#353550] shadow hover:text-[#FC2783] hover:shadow-pink-500/40 transition-all duration-200 hover:bg-white hover:border-pink-500 hover:scale-105 flex items-center gap-2"
                      style={{
                        boxShadow: "0 2px 16px 0 rgba(252,39,131,0.09)",
                        minHeight: "44px",
                      }}
                    >
                      <ImagePlus size={20} className="mr-2" />
                      Generate Product Avatar Image
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                  {avatarImages.map((img) => {
                    const imgKey =
                      img.id ||
                      img.productReplaceResultKey ||
                      img.replaceProductTaskCardId ||
                      img.replaceProductTaskImageId ||
                      img.key ||
                      img.url;

                    const selectedKey =
                      selectedAvatarImage &&
                      (selectedAvatarImage.id ||
                        selectedAvatarImage.productReplaceResultKey ||
                        selectedAvatarImage.replaceProductTaskCardId ||
                        selectedAvatarImage.replaceProductTaskImageId ||
                        selectedAvatarImage.key ||
                        selectedAvatarImage.url);

                    const isSelected =
                      selectedKey && String(selectedKey) === String(imgKey);

                    return (
                      <div
                        key={imgKey}
                        className={`relative border-2 rounded-2xl overflow-hidden cursor-pointer group h-[220px] md:h-[260px]
                          ${
                            isSelected
                              ? "border-pink-500 ring-2 ring-pink-600"
                              : "border-transparent hover:border-pink-400"
                          }`}
                        onClick={() => setSelectedAvatarImage(img)}
                      >
                        <img
                          src={img.url}
                          alt={imgKey || "Avatar"}
                          className="object-cover w-full h-full"
                          onError={(e) => {
                            e.currentTarget.src = "/placeholder.jpg";
                          }}
                        />
                        <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                      </div>
                    );
                  })}
                </div>
              )}

              {imagesError && (
                <div className="text-red-400 text-center mt-2">
                  {imagesError}
                </div>
              )}
            </>
          )}

          {/* Step 2: Script */}
          {step === 2 && (
            <>
              <h2 className="text-xl font-bold text-white mb-4">Script</h2>

              <div className="mb-3 text-white/70 text-xs">
  Scripts are limited to {SCRIPT_CHAR_LIMIT} characters for this format.
</div>

              <div className="flex items-center mb-2">
                <p className="text-gray-300 flex-1">
                  AI-generated script (edit as you wish).
                </p>
                <button
                  onClick={handleGenerateScript}
                  className="ml-2 p-2 text-pink-400 hover:text-pink-300 rounded-full bg-[#232336] hover:bg-[#2c2c3e] transition"
                  disabled={generatingScript}
                  aria-label="Regenerate Script"
                  type="button"
                >
                  <RefreshCcw
                    size={20}
                    className={generatingScript ? "animate-spin" : ""}
                  />
                </button>
              </div>
              {generatingScript && (
                <div className="text-white font-semibold mb-2">
                  Generating script...
                </div>
              )}
              {scriptError && (
                <div className="mb-2 text-red-500 font-semibold">
                  {scriptError}
                </div>
              )}
              <textarea
                className="w-full min-h-[220px] max-h-[330px] bg-[#1F1F42] rounded-xl p-4 text-white border border-gray-600 focus:border-pink-500 focus:ring-1 focus:ring-pink-600 transition resize-none"
                style={{ fontSize: "1.1rem", marginBottom: "1.5rem" }}
                value={script}
                onChange={(e) => setScript(clampScript(e.target.value))}
                disabled={generatingScript}
                placeholder="Script will appear here..."
                maxLength={SCRIPT_CHAR_LIMIT}
              />

              <div className="mt-[-8px] mb-4 flex items-center justify-between">
                <span className="text-xs text-white/60">
                  Maximum {SCRIPT_CHAR_LIMIT} characters
                </span>
                <span
                  className={`text-xs font-medium ${
                    scriptNearLimit ? "text-amber-300" : "text-white/60"
                  }`}
                >
                  {scriptLength} / {SCRIPT_CHAR_LIMIT}
                </span>
              </div>
            </>
          )}

          {/* Step 3: Voice & Captions */}
          {step === 3 && (
            <>
              <h2 className="text-xl font-bold text-white mb-4">
                Select Voice & Captions
              </h2>

              {/* Filters + Caption style on ONE row */}
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div className="flex flex-wrap items-start gap-4">
                  <SingleSelectDropdown
                    options={allLanguages}
                    value={selectedLanguage}
                    onChange={setSelectedLanguage}
                    label="Language"
                    placeholder="All languages"
                    className="w-44"
                  />
                  <SingleSelectDropdown
                    options={allVoiceGenders}
                    value={selectedVoiceGender}
                    onChange={setSelectedVoiceGender}
                    label="Gender"
                    placeholder="All genders"
                    className="w-36"
                  />
                  <SingleSelectDropdown
                    options={allAges}
                    value={selectedAge}
                    onChange={setSelectedAge}
                    label="Age"
                    placeholder="All ages"
                    className="w-36"
                  />
                  {/* Caption style dropdown aligned with the others */}
                  <CaptionDropdown
                    captions={captions}
                    selectedCaptionId={selectedCaptionId}
                    onChange={setSelectedCaptionId}
                    loading={captionsLoading}
                    error={captionError}
                  />
                </div>

                {/* Clear Filters column */}
                <div className="flex flex-col items-end">
                  <span className="block text-xs mb-1 ml-1 opacity-0">
                    Clear
                  </span>
                  <button
                    onClick={() => {
                      setSelectedLanguage("");
                      setSelectedVoiceGender("");
                      setSelectedAge("");
                    }}
                    className="px-3 py-2 bg-pink-600 rounded-md text-white hover:bg-pink-700 text-sm"
                    aria-label="Clear voice filters"
                    type="button"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>

              {/* Voices grid / loader */}
              {isVoicesInitialLoading ? (
                <div
                  className="flex items-center justify-center py-10 sm:py-16"
                  style={{ minHeight: 360 }}
                >
                  <Loader />
                </div>
              ) : voices.length === 0 && !voicesLoading ? (
                <div
                  className="text-white/70 text-center py-6"
                  style={{ minHeight: 360 }}
                >
                  No voices found.
                </div>
              ) : (
                <InfiniteScroll
                  dataLength={voices.length}
                  next={loadMoreVoices}
                  hasMore={hasMoreVoices}
                  loader={
                    voicesLoading && hasMoreVoices ? (
                      <div className="w-full flex justify-center py-4">
                        <Loader />
                      </div>
                    ) : null
                  }
                  scrollableTarget="product-avatar-video-modal-scroll"
                  scrollThreshold={0.85}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                    {voices.map((voice) => {
                      const isSelected =
                        String(selectedVoiceId) === String(voice.id);
                      return (
                        <div
                          key={voice.id}
                          className={`relative rounded-lg border-2 transition cursor-pointer flex flex-col justify-between p-4 group bg-[#23233a] ${
                            isSelected
                              ? "border-pink-500 ring-2 ring-pink-600"
                              : "border-transparent hover:border-pink-400"
                          }`}
                          onClick={() => handleVoiceSelect(voice.id)}
                          aria-label={`Select voice ${
                            voice.name || "Unnamed"
                          }`}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleVoiceSelect(voice.id);
                            }
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-2xl">
                              {LANGUAGE_FLAG_MAP[voice.language] || ""}
                            </span>
                            <span className="font-semibold text-white text-lg truncate">
                              {voice.name}
                            </span>
                          </div>
                          <div className="text-sm text-gray-300 mb-1 space-y-1">
                            <div>
                              <strong>Language:</strong> {voice.language}
                            </div>
                            <div>
                              <strong>Accent:</strong>{" "}
                              {voice.accent || "N/A"}
                            </div>
                            <div>
                              <strong>Gender:</strong>{" "}
                              {voice.gender || "N/A"}
                            </div>
                            <div>
                              <strong>Age:</strong> {voice.age || "N/A"}
                            </div>
                          </div>

                          {voice.preview_url && (
                            <VoicePreviewButton
                              audioSrc={voice.preview_url}
                              voiceId={voice.id}
                              playingAudioId={playingAudioId}
                              onVoicePlay={onVoicePlay}
                              onVoicePause={onVoicePause}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </InfiniteScroll>
              )}

              {voicesLoading && voices.length > 0 && (
                <div className="text-white text-center py-2">Loading...</div>
              )}
              {voiceError && (
                <div className="text-red-400 text-center mt-2">
                  {voiceError}
                </div>
              )}
              {scriptError && (
                <div className="text-red-400 text-center mt-3">
                  {scriptError}
                </div>
              )}
            </>
          )}
        </div>

        {/* --- Sticky Footer Nav --- */}
        <StepFooter
          left={
            step > 1 && (
              <button
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                className="text-white px-7 py-2 rounded-xl font-bold hover:bg-[#222246] transition"
                disabled={
                  (step === 2 && generatingScript) ||
                  (step === 3 && voicesLoading)
                }
                style={{ minWidth: 120 }}
              >
                Previous
              </button>
            )
          }
          right={
            step < 3 ? (
              <button
                className={`bg-pink-600 text-white px-10 py-2 rounded-xl font-bold shadow transition
                  ${
                    (step === 1 &&
                      (!selectedAvatarImage ||
                        imagesLoading ||
                        avatarImages.length === 0)) ||
                    (step === 2 && (!script || generatingScript))
                      ? "opacity-40 cursor-not-allowed"
                      : "hover:bg-pink-500"
                  }`}
                disabled={
                  (step === 1 &&
                    (!selectedAvatarImage ||
                      imagesLoading ||
                      avatarImages.length === 0)) ||
                  (step === 2 && (!script || generatingScript))
                }
                onClick={() => setStep((s) => s + 1)}
                style={{ minWidth: 140 }}
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleGenerate}
                disabled={
                  generating ||
                  !selectedAvatarImage ||
                  !script ||
                  !selectedVoiceId
                }
                className={`bg-pink-600 text-white px-10 py-2 rounded-xl font-bold shadow transition flex items-center justify-center
                  ${
                    generating ||
                    !selectedAvatarImage ||
                    !script ||
                    !selectedVoiceId
                      ? "opacity-40 cursor-not-allowed"
                      : "hover:bg-pink-500"
                  }`}
                style={{ minWidth: 170 }}
              >
                {generating ? "Generating..." : "Generate Video"}
              </button>
            )
          }
        />
      </div>
    </div>
  );
}

/* ---------- Helper components ---------- */

function VoicePreviewButton({
  audioSrc,
  voiceId,
  playingAudioId,
  onVoicePlay,
  onVoicePause,
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const handlePlayPause = (e) => {
    e.stopPropagation();
    const audioEl = audioRef.current;
    if (!audioEl) return;

    if (isPlaying) {
      try {
        audioEl.pause();
      } catch {
        // ignore
      }
      setIsPlaying(false);
      onVoicePause();
    } else {
      try {
        audioEl.currentTime = 0;
      } catch {
        // ignore
      }
      const playPromise = audioEl.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => {
            setIsPlaying(true);
            onVoicePlay(audioRef, voiceId);
          })
          .catch(() => {
            setIsPlaying(false);
            onVoicePause();
          });
      } else {
        setIsPlaying(true);
        onVoicePlay(audioRef, voiceId);
      }
    }
  };

  useEffect(() => {
    if (playingAudioId !== voiceId && isPlaying) {
      const audioEl = audioRef.current;
      if (audioEl) {
        try {
          audioEl.pause();
        } catch {
          // ignore
        }
      }
      setIsPlaying(false);
    }
  }, [playingAudioId, voiceId, isPlaying]);

  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    const handleEnded = () => {
      setIsPlaying(false);
      onVoicePause();
    };

    audioEl.addEventListener("ended", handleEnded);
    return () => {
      audioEl.removeEventListener("ended", handleEnded);
      try {
        audioEl.pause();
      } catch {
        // ignore
      }
    };
  }, [audioSrc, onVoicePause]);

  return (
    <>
      <audio ref={audioRef} src={audioSrc} preload="none" />
      <button
        type="button"
        className="mt-auto bg-pink-600 hover:bg-pink-700 rounded-full p-2 flex items-center justify-center self-start"
        onClick={handlePlayPause}
        aria-label={
          isPlaying ? "Pause voice preview" : "Play voice preview"
        }
      >
        {isPlaying ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 9v6m4-6v6"
            />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-white"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M6 4l12 6-12 6V4z" />
          </svg>
        )}
      </button>
    </>
  );
}

// Caption dropdown with thumbnail preview
function CaptionDropdown({
  captions,
  selectedCaptionId,
  onChange,
  loading,
  error,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  if (loading) {
    return (
      <div className="flex flex-col gap-1 w-36">
        <label className="block text-xs text-gray-300 mb-1 ml-1">
          Caption style
        </label>
        <div className="text-gray-400 text-sm">Loading captions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-1 w-36">
        <label className="block text-xs text-gray-300 mb-1 ml-1">
          Caption style
        </label>
        <div className="text-red-400 text-xs mt-1">{error}</div>
      </div>
    );
  }

  if (!captions || captions.length === 0) {
    return (
      <div className="flex flex-col gap-1 w-36">
        <label className="block text-xs text-gray-300 mb-1 ml-1">
          Caption style
        </label>
        <div className="text-gray-400 text-sm">
          No captions available (optional).
        </div>
      </div>
    );
  }

  const selected = captions.find(
    (c) => String(c.id) === String(selectedCaptionId)
  );

  return (
    <div className="relative w-36" ref={ref}>
      <label className="block text-xs text-gray-300 mb-1 ml-1">
        Caption style
      </label>
      <button
        type="button"
        className={`
          flex w-full items-stretch px-3 py-2 rounded-lg bg-[#23233a] text-white border
          transition-all outline-none focus:ring-2 focus:ring-pink-600
          ${open ? "border-pink-500 ring-2 ring-pink-600" : "border-[#353550]"}
        `}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? (
          <div className="flex flex-col flex-1 min-w-0 gap-2 text-left">
            <span className="text-sm font-medium truncate">
              {selected.name}
            </span>
            {selected.thumbnail ? (
              <div className="w-full h-[56px] rounded-md border border-[#353550] bg-black/60 flex items-center justify-center overflow-hidden">
                <img
                  src={selected.thumbnail}
                  alt={selected.name}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
            ) : null}
          </div>
        ) : (
          <span className="text-gray-400 text-sm flex-1 text-left">
            Select caption style
          </span>
        )}
        <svg
          className={`ml-3 mt-1 flex-shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          width="18"
          height="18"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <ul
          className="absolute z-40 mt-1 w-full rounded-xl shadow-xl bg-[#212134] border border-[#353550] max-h-72 overflow-auto animate-fadeIn"
          role="listbox"
          tabIndex={-1}
        >
          {captions.map((c) => {
            const isSelected = String(selectedCaptionId) === String(c.id);
            return (
              <li
                key={c.id}
                className={`cursor-pointer px-3 py-2 text-sm select-none ${
                  isSelected
                    ? "bg-pink-600/10 text-pink-300"
                    : "hover:bg-[#2e2e45]"
                }`}
                onClick={() => {
                  onChange(String(c.id));
                  setOpen(false);
                }}
                role="option"
                aria-selected={isSelected}
              >
                <div className="flex flex-col gap-2 w-full text-left">
                  <span className="font-medium">{c.name}</span>
                  {c.thumbnail ? (
                    <div className="w-full h-[56px] rounded-md border border-[#353550] bg-black/60 flex items-center justify-center overflow-hidden">
                      <img
                        src={c.thumbnail}
                        alt={c.name}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Single-select dropdown identical to AdMarketing
function SingleSelectDropdown({
  options,
  value = "",
  onChange,
  label,
  placeholder = "Select...",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const getLabel = (val) => {
    if (!val) return null;
    const opt = options.find((o) => String(o.value) === String(val));
    return opt ? opt.label : val;
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <label className="block text-xs text-gray-300 mb-1 ml-1">{label}</label>
      <button
        type="button"
        className={`
          flex w-full min-h-[38px] items-center px-3 py-1.5 rounded-lg bg-[#23233a] text-white border
          transition-all outline-none focus:ring-2 focus:ring-pink-600
          ${open ? "border-pink-500 ring-2 ring-pink-600" : "border-[#353550]"}
        `}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className="flex flex-1 items-center text-left">
          {value ? (
            <span className="truncate">{getLabel(value)}</span>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </div>
        <svg
          className={`ml-2 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          width="18"
          height="18"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {open && (
        <ul
          className="absolute z-40 mt-1 w-full rounded-xl shadow-xl bg-[#212134] border border-[#353550] max-h-56 overflow-auto animate-fadeIn"
          role="listbox"
          tabIndex={-1}
        >
          <li
            key="all"
            className={`cursor-pointer px-3 py-2 text-sm select-none ${
              !value
                ? "bg-pink-600/10 text-pink-300"
                : "hover:bg-[#2e2e45]"
            }`}
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            role="option"
            aria-selected={!value}
          >
            All
          </li>
          {options.length === 0 && (
            <li className="p-3 text-gray-400">No options</li>
          )}
          {options.map((opt) => {
            const selected = String(value) === String(opt.value);
            return (
              <li
                key={opt.value}
                className={`cursor-pointer px-3 py-2 text-sm select-none ${
                  selected
                    ? "bg-pink-600/10 text-pink-300"
                    : "hover:bg-[#2e2e45]"
                }`}
                onClick={() => {
                  onChange(String(opt.value));
                  setOpen(false);
                }}
                role="option"
                aria-selected={selected}
              >
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
